import { useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Paper,
} from "@mui/material";
import {
  HelpOutline,
  Assignment as PtwIcon,
  Visibility as ObservationIcon,
  LocationOn as SiteIcon,
  Add as NewWorkflowIcon,
  Edit as EditWorkflowIcon,
  FileCopy as DuplicateWorkflowIcon,
  ArrowForward as ArrowIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

// Import individual guide dialogs
import PtwGuideDialog from "@/components/dialogs/PtwGuideDialog";
import ObservationGuideDialog from "@/components/dialogs/ObservationGuideDialog";
import SitesGuideDialog from "@/components/dialogs/SitesGuideDialog";
import NewWorkflowUserGuide from "@/components/dialogs/NewWorkflowUserGuide";
import EditWorkflowUserGuide from "@/components/dialogs/EditWorkflowUserGuide";
import DuplicateWorkflowUserGuide from "@/components/dialogs/DuplicateWorkflowUserGuide";
import HazardsGuideDialog from "@/components/dialogs/HazardsGuideDialog";
import PrecautionsGuideDialog from "@/components/dialogs/PrecautionsGuideDialog";

const UserGuide = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [currentGuide, setCurrentGuide] = useState(null);

  const guides = [
    {
      id: "ptw",
      title: t("userGuide.ptwGuideTitle"),
      description: t("userGuide.ptwGuideDescription"),
      icon: <PtwIcon />,
      color: theme.palette.primary.main,
      component: PtwGuideDialog,
    },
    {
      id: "observation",
      title: t("userGuide.observationGuideTitle"),
      description: t("userGuide.observationGuideDescription"),
      icon: <ObservationIcon />,
      color: theme.palette.error.main,
      component: ObservationGuideDialog,
    },
    {
      id: "sites",
      title: t("userGuide.sitesGuideTitle"),
      description: t("userGuide.sitesGuideDescription"),
      icon: <SiteIcon />,
      color: theme.palette.info.main,
      component: SitesGuideDialog,
    },
    {
      id: "newWorkflow",
      title: t("userGuide.newWorkflowGuideTitle"),
      description: t("userGuide.newWorkflowGuideDescription"),
      icon: <NewWorkflowIcon />,
      color: theme.palette.success.main,
      component: NewWorkflowUserGuide,
    },
    {
      id: "editWorkflow",
      title: t("userGuide.editWorkflowGuideTitle"),
      description: t("userGuide.editWorkflowGuideDescription"),
      icon: <EditWorkflowIcon />,
      color: theme.palette.warning.main,
      component: EditWorkflowUserGuide,
    },
    {
      id: "duplicateWorkflow",
      title: t("userGuide.duplicateWorkflowGuideTitle"),
      description: t("userGuide.duplicateWorkflowGuideDescription"),
      icon: <DuplicateWorkflowIcon />,
      color: theme.palette.secondary.main,
      component: DuplicateWorkflowUserGuide,
    },
    {
      id: "hazards",
      title: t("userGuide.hazardsGuideTitle"),
      description: t("userGuide.hazardsGuideDescription"),
      icon: <WarningIcon />,
      color: theme.palette.warning.main,
      component: HazardsGuideDialog,
    },
    {
      id: "precautions",
      title: t("userGuide.precautionsGuideTitle"),
      description: t("userGuide.precautionsGuideDescription"),
      icon: <SecurityIcon />,
      color: theme.palette.success.main,
      component: PrecautionsGuideDialog,
    },
  ];

  const handleGuideSelect = (guide) => {
    setCurrentGuide(guide);
  };

  const handleCloseGuide = () => {
    setCurrentGuide(null);
  };

  // Render individual guide dialog
  if (currentGuide) {
    const GuideComponent = currentGuide.component;
    return (
      <GuideComponent
        open={true}
        onClose={handleCloseGuide}
        onBack={handleCloseGuide}
      />
    );
  }

  // Render main guide page
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          minHeight: "80vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
            p: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                border: `2px solid ${alpha(theme.palette.primary.contrastText, 0.3)}`,
              }}
            >
              <HelpOutline sx={{ fontSize: 36 }} />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {t("userGuide.mainTitle")}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {t("userGuide.mainSubtitle")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.7, fontSize: "1.1rem" }}
          >
            {t("userGuide.mainDescription")}
          </Typography>

          <List sx={{ width: "100%" }}>
            {guides.map((guide, index) => (
              <Box key={guide.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleGuideSelect(guide)}
                    sx={{
                      borderRadius: 3,
                      mb: 2,
                      p: 3,
                      "&:hover": {
                        bgcolor: alpha(guide.color, 0.08),
                        transform: "translateX(8px)",
                      },
                      transition: "all 0.3s ease",
                      border: `1px solid ${alpha(guide.color, 0.2)}`,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: guide.color,
                        minWidth: 80,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          bgcolor: alpha(guide.color, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `2px solid ${alpha(guide.color, 0.3)}`,
                        }}
                      >
                        {guide.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                        >
                          {guide.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{ color: "text.secondary", lineHeight: 1.6 }}
                        >
                          {guide.description}
                        </Typography>
                      }
                    />
                    <ArrowIcon
                      sx={{
                        color: guide.color,
                        ml: 2,
                        fontSize: 28,
                        opacity: 0.7,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < guides.length - 1 && (
                  <Divider sx={{ mx: 3, opacity: 0.3 }} />
                )}
              </Box>
            ))}
          </List>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserGuide;
